import React from 'react';
import PropTypes from 'prop-types';
import { FileDrop } from 'react-file-drop';
import { Button, Checkbox, Divider, Icon, List, Message, Segment } from 'semantic-ui-react';
import _ from 'lodash';
import ReactQuill from 'react-quill';
import { uploadToS3 } from '../../utils/s3-service';
import hasRESTClient from '../../hocs/has-rest-client';

const quillStyle = {
  minHeight: 100,
};

const uploadFileBlock = {
  width: '100%',
  height: 70,
  marginTop: 10,
  border: '1px solid #777',
  textAlign: 'center',
  paddingTop: 25,
  textTransform: 'uppercase',
  borderRadius: 5,
};

const titleStyle = {
  paddingBottom: 5,
  display: 'block',
};

const hiddenStyle = {
  display: 'none',
};

const checkboxStyle = {
  marginTop: 10,
  marginBottom: 10,
};

class CampaignOverviewSettings extends React.Component {
  //
  constructor(props) {
    super(props);
    this.state = {
      savedOk: false,
      isUploading: false,
      documents: [],
      overview: {},
    };
    this.fileRef = null;
  }

  componentDidMount = async () => {
    const { restClient, campaignId } = this.props;
    const response = await restClient.get(`campaigns/${campaignId}?include=documents`);
    const overview = _.get(response, 'data.data.attributes.overview', {});
    const documents = _.get(response, 'data.included', []);
    this.setState({
      overview,
      documents,
    });
  }

  onTargetClick = () => {
    this.fileRef.click();
  }

  onDrop = async files => {
    await this.processCampaignDocuments(files);
  }

  onFileInputChanged = async event => {
    await this.processCampaignDocuments(_.get(event, 'target.files', []));
  }

  deleteFile = async documentId => {
    const { restClient, campaignId } = this.props;
    const { documents } = this.state;
    const updatedDocuments = _.reject(documents, ['id', documentId]);
    const payload = _.map(updatedDocuments, document => _.pick(document, ['id', 'type']));
    await restClient.patch(`campaigns/${campaignId}/relationships/documents`, {
      data: payload,
    });
    await restClient.delete(`documents/${documentId}`);
    this.setState({
      documents: updatedDocuments,
    });
  }

  processCampaignDocuments = async (files = []) => {
    if (_.isEmpty(files)) return;

    const { campaignId, restClient } = this.props;
    this.setState({
      isUploading: true,
    });
    // eslint-disable-next-line no-restricted-syntax
    for (const fileToBeUploaded of files) {
      // eslint-disable-next-line no-await-in-loop
      const uploadedFilePath = await uploadToS3([fileToBeUploaded]);
      const attributes = {
        campaign: {
          id: campaignId,
          type: 'campaigns',
        },
        name: _.get(fileToBeUploaded, 'name'),
        fileType: _.get(fileToBeUploaded, 'type'),
        filePath: _.last(uploadedFilePath),
      };
      // eslint-disable-next-line no-await-in-loop
      const createdDocument = await restClient.post('documents', {
        data: { attributes },
      });
      const existingDocuments = _.clone(this.state.documents);
      existingDocuments.push(_.get(createdDocument, 'data.data'));
      this.setState({
        documents: existingDocuments,
      }, this.onSaveButtonClick);
    }
    this.setState({
      isUploading: false,
    });
  }

  onQuillValueChanged = value => {
    const overview = _.clone(this.state.overview);
    overview.text = value;
    this.setState({
      overview,
    });
  }

  onSaveButtonClick = async () => {
    const {
      campaignId,
      restClient,
    } = this.props;
    const {
      documents,
      overview,
    } = this.state;
    const documentsPatch = _.map(documents, document => _.pick(document, ['type', 'id']));
    await restClient.patch(`campaigns/${campaignId}`, {
      data: {
        attributes: {
          overview,
          documents: documentsPatch,
        },
      },
    });
    this.setState({
      savedOk: true,
    });
  }

  toggleCheckbox = (event, { checked }) => {
    const overview = _.clone(this.state.overview);
    overview.shouldRender = checked;
    this.setState({
      overview,
    });
  }

  render() {
    //
    const {
      documents,
      overview,
      isUploading,
      savedOk,
    } = this.state;
    return (
      <Segment>
        <h3 style={titleStyle}>Campaign Overview</h3>
        <ReactQuill
          placeholder="campaign overview..."
          style={quillStyle}
          value={_.get(overview, 'text', '')}
          onChange={this.onQuillValueChanged}
        />

        <Divider hidden />
        <h3 style={titleStyle}>Supporting Documents</h3>

        <List verticalAlign="middle" relaxed fluid>
          {
            _.map(documents, document => (
              <List.Item>
                <List.Icon name="file pdf outline" />
                <List.Content>
                  { _.get(document, 'attributes.name', '-') }
                  <Icon
                    link
                    name="delete"
                    color="red"
                    onClick={() => this.deleteFile(_.get(document, 'id'))}
                  />
                </List.Content>
              </List.Item>
            ))
          }
        </List>

        <FileDrop
          onTargetClick={this.onTargetClick}
          onDrop={this.onDrop}
          targetAlwaysVisible
        >
          <div style={uploadFileBlock}>
            {
              isUploading && <div><Icon name="refresh" loading /> Uploading... Please wait</div>
            }
            { !isUploading && <div>drag files or click here</div> }
          </div>
        </FileDrop>
        <input
          accept="application/pdf"
          onChange={this.onFileInputChanged}
          ref={input => { this.fileRef = input; }}
          type="file"
          style={hiddenStyle}
        />
        <Divider hidden fitted />
        {
          savedOk &&
          <Message success>
            Changes saved
          </Message>
        }
        <Divider hidden fitted />
        <Checkbox
          checked={_.get(overview, 'shouldRender', false)}
          onChange={this.toggleCheckbox}
          label="display overview on client reports"
          style={checkboxStyle}
        />
        <Divider hidden fitted />
        <Button onClick={this.onSaveButtonClick} color="pink">Save</Button>
      </Segment>
    );
  }
}

CampaignOverviewSettings.propTypes = {
  restClient: PropTypes.object.isRequired,
};

export default hasRESTClient(CampaignOverviewSettings);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-overview-settings/index.js
 **/